import { apiBase, jwtName } from '@config'
import { getterFinder, getterOptions } from '@store/utils'
import authentication from '@feathersjs/authentication-client'
import { checkRoute } from '@router/authorization'
import client from '@store/client'
import { pluck } from 'rambda'
import router from '@router'

client.configure(authentication({
  path: `${apiBase}/authentication`,
  storageKey: jwtName,
}))

export default {
  store: {
    state: {
      hasAuthChecked: false,
      hasAuthStarted: false,
      isLoggingOut: false,
      userId: null,
    },

    actions: {
      CHECK_LOGIN: async ({ commit, dispatch, state }) => {
        if (state.isLoggingOut || state.hasAuthStarted) return

        commit('SET_AUTH_STARTED')

        try {
          const payload = await client.reAuthenticate()
          dispatch('SET_PROFILE', payload.user)
        } catch (error) {
          if (error.name === 'NotAuthenticated') {
            return
          } else if (error.name === 'NotFound') {
            dispatch('LOGOUT')
            return
          }

          throw error
        } finally {
          commit('SET_AUTH_CHECKED')
        }
      },

      LOGIN: async ({ dispatch }, loginDetails) => {
        const { user } = await client.authenticate({
          strategy: 'local',
          ...loginDetails,
        })

        dispatch('SET_PROFILE', user)
      },

      LOGOUT: async ({ commit, dispatch }) => {
        commit('SET_LOGGING_OUT', true)

        await client.logout()
        client.emit('logout') // forces to restart user connection

        delete localStorage[jwtName]

        commit('SET_AUTH_USER_ID', null)
        commit('SET_LOGGING_OUT', false)

        // conditional that ignores unit testing as it does not have a router
        if (router.app.$route) {
          checkRoute(router.app.$route, router.app.$route)
        }

        dispatch('CLEAR_STATE')
        dispatch('CLEAR_PAGINATIONS')
      },

      SET_AUTH_CHECKED({ commit }) {
        commit('SET_AUTH_CHECKED')
      },

      SET_AUTH_STARTED({ state, commit }) {
        if (!state.hasAuthStarted) {
          commit('SET_AUTH_STARTED')
        }
      },

      SET_PROFILE({ commit }, user) {
        commit('ADD_USER', user)
        commit('SET_AUTH_USER_ID', user.id)
      },
    },

    mutations: {
      SET_AUTH_STARTED: (state) => {
        state.hasAuthStarted = true
      },

      SET_AUTH_CHECKED: (state) => {
        state.hasAuthChecked = true
      },

      SET_LOGGING_OUT: (state, isLoggingOut) => {
        state.isLoggingOut = isLoggingOut
      },

      SET_AUTH_USER_ID: (state, userId) => {
        state.userId = userId
      },
    },

    getters: {
      authRedirect: (_, { hasRole }) => hasRole('>=USER')
        ? 'Articles'
        : 'Index',
      authUser: ({ userId }, { query }) => userId
        ? query('user', userId)
        : null,
      authUserSettings: (_, { isAuth, authUser }) => isAuth
        ? authUser.settings
        : {},

      authHeaders: () => {
        const Authorization = localStorage[jwtName]
        return Authorization ? { Authorization } : {}
      },

      hasAuthChecked: state => state.hasAuthChecked,
      hasAuthToken: () => () => !!localStorage[jwtName],
      isAuth: (_, { authUser }) => !!authUser,

      roles: () => [
        { label: 'Super Administrator', value: 'SUPERADMIN' },
        { label: 'Administrator', value: 'ADMIN' },
        { label: 'Manager', value: 'MANAGER' },
        { label: 'Support', value: 'SUPPORT' },
        { label: 'Moderator', value: 'MODERATOR' },
        { label: 'Member', value: 'MEMBER' },
        { label: 'User', value: 'USER' },
      ],
      roleKeys: (_, getters) => pluck('value', getters.roles),
      roleOptions: getterOptions('roles'),
      getRole: getterFinder('roles'),

      parseRole: (_, { roleKeys }) => (role) => {
        if (role === '*') return [...roleKeys]

        const removeSymbols = string => string.replace(/[!<=>]/g, '')
        const roleClean = removeSymbols(role)

        if (roleClean.length === role.length) return [role]

        if (!roleKeys.includes(roleClean)) {
          throw new Error(`Invalid role name ${roleClean}`)
        }

        const roleIndex = roleKeys.indexOf(roleClean)

        if (role.includes('>=')) {
          return roleKeys.slice(0, roleIndex + 1)
        } else if (role.includes('>')) {
          return roleKeys.slice(0, roleIndex)
        } else if (role.includes('<=')) {
          return roleKeys.slice(roleIndex)
        } else if (role.includes('<')) {
          return roleKeys.slice(roleIndex + 1)
        } else if (role.includes('!=')) {
          return roleKeys.filter((_, index) => index !== roleIndex)
        }

        return [roleClean]
      },

      hasRole: (_, { isAuth, authUser, parseRole }) => (...roles) =>
        isAuth && roles
          .flatMap(parseRole)
          .some(role => parseRole(role).includes(authUser.role)),
    },
  },
}
